// App.js
import React, { useState, useEffect } from 'react';
import web3Service from './services/Web3Service';
import GameComponent from './components/GameComponent';
import KenoComponent from './components/KenoComponent';
import NavigationComponent from './NavigationComponent';
import SpaceEffects from './spaceEffects';
import WalletSelectorComponent from './components/WalletSelectorComponent';
import ensureBigIntSupport from './bigIntPolyFill';
import './App.css';

const App = () => {
  // Web3 connection states
  const [account, setAccount] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('Connecting...');
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Game selection state
  const [currentGame, setCurrentGame] = useState('coinflip'); // 'coinflip' or 'keno'
  
  // Initialize Web3 connection
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Ensure BigInt support
        ensureBigIntSupport();
        
        // We don't auto-initialize here anymore
        // Instead, we'll wait for the user to select a wallet
        setConnectionStatus('Please connect your wallet');
      } catch (error) {
        console.error('Error initializing app:', error);
        setConnectionStatus('Initialization Error: ' + error.message);
      }
    };
    
    initializeApp();
    
    // Clean up on component unmount
    return () => {
      web3Service.cleanup();
    };
  }, []);
  
  // Update account and connection status when they change in web3Service
  useEffect(() => {
    const updateInterval = setInterval(() => {
      if (web3Service.account !== account) {
        setAccount(web3Service.account);
      }
      
      if (web3Service.connectionStatus !== connectionStatus && web3Service.connectionStatus !== 'Connecting...') {
        setConnectionStatus(web3Service.connectionStatus);
      }
    }, 1000);
    
    return () => clearInterval(updateInterval);
  }, [account, connectionStatus]);
  
  // Handle wallet connection
  const handleWalletConnected = () => {
    setAccount(web3Service.account);
    setConnectionStatus(web3Service.connectionStatus);
    setIsInitialized(true);
  };
  
  // Handle game change
  const handleGameChange = (game) => {
    setCurrentGame(game);
  };
  
  // Render the appropriate game component based on currentGame
  const renderGameComponent = () => {
    if (!isInitialized) return null;
    
    switch (currentGame) {
      case 'keno':
        return <KenoComponent />;
      case 'coinflip':
      default:
        return <GameComponent />;
    }
  };
  
  return (
    <div className="coin-flip-app">
      <SpaceEffects />
      <div className="app-container">
        <NavigationComponent 
          currentGame={currentGame} 
          onGameChange={handleGameChange} 
        />
        
        {!account ? (
          <WalletSelectorComponent onConnect={handleWalletConnected} />
        ) : (
          <>
            <div className="account-info">
              {web3Service.formatAddress(account)}
              <div className="connection-status">
                <span className={`status-badge ${
                  connectionStatus === 'Connected' ? 'status-connected' : 
                  connectionStatus.includes('Error') ? 'status-error' : 'status-warning'
                }`}>
                  {connectionStatus}
                </span>
              </div>
            </div>
            
            {renderGameComponent()}
          </>
        )}
      </div>
    </div>
  );
};

export default App;